import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';

export interface TriviaClueOption {
  text: string;
  difficulty: 'easy' | 'medium' | 'hard';
  sourceConfidence: 'fact-grounded' | 'ai-memory' | 'custom-source';
  cachedVerification?: {
    verdict: 'true' | 'false' | 'uncertain';
    explanation: string;
    verifiedAt: string;
  } | null;
}

export interface TriviaClue {
  id: string;
  eventId: string;
  djId: string;
  songRequestId: string;
  songTitle: string;
  songArtist: string;
  songAlbumArtUrl?: string;
  clueText: string;
  clueSource: 'ai' | 'custom';
  difficulty?: 'easy' | 'medium' | 'hard';
  locked: boolean;
  skipped: boolean;
  timesUsed: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClueVerification {
  verdict: 'true' | 'false' | 'uncertain';
  explanation: string;
  sources: Array<{ name: string; detail: string }>;
  correctedText: string | null;
}

function docToTriviaClue(docSnap: any): TriviaClue {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    songRequestId: data.songRequestId,
    songTitle: data.songTitle,
    songArtist: data.songArtist,
    songAlbumArtUrl: data.songAlbumArtUrl || undefined,
    clueText: data.clueText || '',
    clueSource: data.clueSource || 'ai',
    difficulty: data.difficulty || undefined,
    locked: data.locked || false,
    skipped: data.skipped || false,
    timesUsed: data.timesUsed || 0,
    createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt || '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || data.updatedAt || '',
  };
}

export async function generateClueOptions(title: string, artist: string): Promise<TriviaClueOption[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication required');
  const idToken = await user.getIdToken();

  const response = await fetch('/api/generateTriviaClues', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ title: title.trim(), artist: artist.trim() }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Clue generation failed (${response.status})`);
  }

  const data = await response.json();
  return data.clues || [];
}

export async function verifyClue(title: string, artist: string, clueText: string): Promise<ClueVerification> {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication required');
  const idToken = await user.getIdToken();

  const response = await fetch('/api/verifyTriviaClue', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ title: title.trim(), artist: artist.trim(), clueText }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Clue verification failed (${response.status})`);
  }

  return response.json();
}

export async function saveClueVerification(
  title: string,
  artist: string,
  clueText: string,
  verdict: 'true' | 'false' | 'uncertain',
  explanation: string
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication required');
  const idToken = await user.getIdToken();

  const response = await fetch('/api/saveClueVerification', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ title: title.trim(), artist: artist.trim(), clueText, verdict, explanation }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as { error?: string }).error || `Save verification failed (${response.status})`);
  }
}

export async function generateCluesFromFacts(
  title: string,
  artist: string,
  facts: string
): Promise<TriviaClueOption[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('Authentication required');
  const idToken = await user.getIdToken();

  const response = await fetch('/api/generateCluesFromFacts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ title: title.trim(), artist: artist.trim(), facts }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as { error?: string }).error || `Clue generation from facts failed (${response.status})`);
  }

  const data = await response.json() as { clues?: TriviaClueOption[] };
  return data.clues || [];
}

export async function getPastLockedCluesForSong(
  djId: string,
  songTitle: string,
  currentEventId: string
): Promise<TriviaClue[]> {
  const q = query(
    collection(db, 'triviaClues'),
    where('djId', '==', djId),
    where('songTitle', '==', songTitle),
    where('locked', '==', true)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(docToTriviaClue)
    .filter(c => c.eventId !== currentEventId && !c.skipped);
}

export async function lockClue(
  eventId: string,
  djId: string,
  songRequestId: string,
  songData: { title: string; artist: string; albumArtUrl?: string },
  clueText: string,
  source: 'ai' | 'custom',
  difficulty?: 'easy' | 'medium' | 'hard'
): Promise<string> {
  const docId = `${eventId}_${songRequestId}`;
  const clueRef = doc(db, 'triviaClues', docId);

  await setDoc(clueRef, {
    eventId,
    djId,
    songRequestId,
    songTitle: songData.title,
    songArtist: songData.artist,
    songAlbumArtUrl: songData.albumArtUrl || null,
    clueText,
    clueSource: source,
    difficulty: difficulty || null,
    locked: true,
    skipped: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return docId;
}

export async function skipSong(eventId: string, songRequestId: string): Promise<void> {
  const docId = `${eventId}_${songRequestId}`;
  const clueRef = doc(db, 'triviaClues', docId);

  await setDoc(clueRef, {
    eventId,
    songRequestId,
    clueText: '',
    locked: false,
    skipped: true,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function getLockedClues(eventId: string): Promise<TriviaClue[]> {
  const q = query(
    collection(db, 'triviaClues'),
    where('eventId', '==', eventId),
    where('locked', '==', true),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(docToTriviaClue)
    .filter(c => !c.skipped);
}

export async function getClueForSong(eventId: string, songRequestId: string): Promise<TriviaClue | null> {
  const docId = `${eventId}_${songRequestId}`;
  const docSnap = await getDoc(doc(db, 'triviaClues', docId));
  if (!docSnap.exists()) return null;
  return docToTriviaClue(docSnap);
}

export function subscribeToLockedClues(eventId: string, callback: (clues: TriviaClue[]) => void): Unsubscribe {
  const q = query(
    collection(db, 'triviaClues'),
    where('eventId', '==', eventId),
    where('locked', '==', true),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, (snapshot) => {
    const clues = snapshot.docs.map(docToTriviaClue).filter(c => !c.skipped);
    callback(clues);
  });
}
